/**
 * Pure helper to compute dependency connector edges for the Plan timeline PDF export.
 *
 * Mirrors the on-screen overlay logic in ItemsTimeline.tsx (~L641-760):
 *  - anchor edge = type==='SS' ? 'start' : 'end'   (on PRED bar)
 *  - target edge = type==='FF' ? 'end'   : 'start' (on SUCC bar)
 *  - lagDays    = type==='FS' ? lag + 1 : lag       (FS +1 to clear pred's right edge)
 *  - waypoint X = dateToIndex(anchorDate + lagDays) * dayWidth (or +1 for 'end' edge)
 *  - actualX    = real SUCC edge X (target edge of SUCC's real date)
 *
 * NOTE: This file is consumed by the PDF export only. ItemsTimeline.tsx keeps its
 * existing inlined logic untouched to guarantee visual stability.
 */

import type { DependsOnRef } from '@/hooks/useDependsOn';

export type DepType = 'FS' | 'SS' | 'FF';

export interface DepEdge {
  key: string;
  anchorX: number;
  anchorRowKey: string;
  targetX: number;
  actualX: number | null;
  targetRowKey: string;
  type: DepType;
  lag: number;
}

export interface DateColumnLite {
  date: string;
}

interface ComputeParams {
  items: Array<{ id: string; startDate?: string; endDate?: string }>;
  milestonesByItem?: Record<string, Array<{ id: string; start_date?: string; end_date?: string | null }>>;
  itemDeps?: Map<string, DependsOnRef[]>;
  msDeps?: Map<string, DependsOnRef[]>;
  dateColumns: DateColumnLite[];
  dayWidth: number;
}

const ymdLocal = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
};

const addDays = (dateKey: string, days: number): string => {
  const d = new Date(dateKey + 'T00:00:00');
  d.setDate(d.getDate() + (days | 0));
  return ymdLocal(d);
};

const safeYmd = (input: string | null | undefined): string | null => {
  if (!input) return null;
  try {
    const d = new Date(input);
    if (isNaN(d.getTime())) return null;
    return ymdLocal(d);
  } catch {
    return null;
  }
};

export function computeDependencyEdges({
  items,
  milestonesByItem,
  itemDeps,
  msDeps,
  dateColumns,
  dayWidth,
}: ComputeParams): DepEdge[] {
  if (!dateColumns?.length || !(dayWidth > 0)) return [];

  const dateToIndex = (key: string): number =>
    dateColumns.findIndex(col => col.date === key);

  // Build dates lookup for all items + milestones
  const dates = new Map<string, { start: string; end: string }>();
  for (const it of items || []) {
    if (!it?.id) continue;
    const s = safeYmd(it.startDate);
    const e = safeYmd(it.endDate) ?? s;
    if (s && e) dates.set(`item:${it.id}`, { start: s, end: e });
  }
  for (const list of Object.values(milestonesByItem || {})) {
    for (const m of (list as any[]) || []) {
      if (!m?.id) continue;
      const s = safeYmd(m.start_date);
      const e = safeYmd(m.end_date) ?? s;
      if (s && e) dates.set(`milestone:${m.id}`, { start: s, end: e });
    }
  }

  const rowKeyOf = (kind: 'item' | 'milestone', id: string): string =>
    kind === 'item' ? `item-${id}` : `ms-${id}`;

  const edges: DepEdge[] = [];

  const process = (
    succKind: 'item' | 'milestone',
    succId: string,
    deps?: DependsOnRef[],
  ) => {
    if (!deps || deps.length === 0) return;
    const succInfo = dates.get(`${succKind}:${succId}`);
    if (!succInfo) return;

    for (const dep of deps) {
      if (!dep || (dep.kind !== 'item' && dep.kind !== 'milestone') || !dep.id) continue;
      const type: DepType = dep.type === 'SS' || dep.type === 'FF' ? dep.type : 'FS';
      const lag = typeof dep.lag === 'number' && Number.isFinite(dep.lag) ? dep.lag : 0;
      const predInfo = dates.get(`${dep.kind}:${dep.id}`);
      if (!predInfo) continue;

      const anchorEdge: 'start' | 'end' = type === 'SS' ? 'start' : 'end';
      const targetEdge: 'start' | 'end' = type === 'FF' ? 'end' : 'start';
      const anchorDate = anchorEdge === 'start' ? predInfo.start : predInfo.end;

      const aIdxPred = dateToIndex(anchorDate);
      if (aIdxPred < 0) continue; // pred edge outside page
      const anchorX = anchorEdge === 'end' ? (aIdxPred + 1) * dayWidth : aIdxPred * dayWidth;

      const lagDays = type === 'FS' ? lag + 1 : lag;
      const computed = addDays(anchorDate, lagDays);
      const tIdx = dateToIndex(computed);
      if (tIdx < 0) continue; // waypoint outside page → skip
      const targetX = targetEdge === 'end' ? (tIdx + 1) * dayWidth : tIdx * dayWidth;

      const actualDate = targetEdge === 'end' ? succInfo.end : succInfo.start;
      const aIdxSucc = dateToIndex(actualDate);
      const actualX =
        aIdxSucc < 0
          ? null
          : targetEdge === 'end'
            ? (aIdxSucc + 1) * dayWidth
            : aIdxSucc * dayWidth;

      edges.push({
        key: `${dep.kind}:${dep.id}->${succKind}:${succId}:${type}:${lag}`,
        anchorX,
        anchorRowKey: rowKeyOf(dep.kind, dep.id),
        targetX,
        actualX,
        targetRowKey: rowKeyOf(succKind, succId),
        type,
        lag,
      });
    }
  };

  for (const it of items || []) {
    if (!it?.id) continue;
    process('item', it.id, itemDeps?.get(it.id));
  }
  for (const list of Object.values(milestonesByItem || {})) {
    for (const m of (list as any[]) || []) {
      if (!m?.id) continue;
      process('milestone', m.id, msDeps?.get(m.id));
    }
  }

  return edges;
}
